<template>
  <div>
    <PageHeader title="Remuneration" />
    <div class="mb-4 flex flex-col sm:flex-row justify-between gap-y-4 sm:gap-y-0">
      <div class="flex flex-col sm:flex-row w-full gap-y-4 sm:gap-10 items-center">
        <MonthlyDateFilterSelector
          :key="periodSelectorKey"
          class="w-1/4"
          placeholder="Sélectionner une période"
          @newDateSelected="updateDateFilters"
        />
        <SearchBarFilter
          v-for="(filterCategory, filtersName, filterIndex) in availableFiltersWithOptions"
          :key="filterIndex"
          :filterName="filtersName"
          :filterIdx="filterIndex"
          :current-page="currentPage"
          :available-filters="filters"
          :selected-filters="currentFilteringData"
          @updateFilterSearch="updateCurrentFilteringData"
        />
        <ClearFiltersButton v-if="hasActiveFilters" @click="clearFilters" />
      </div>
      <SecondaryButton @click="exportSummary">Exporter</SecondaryButton>
    </div>
    <IbanMissing v-if="ibanMissing" class="mb-4" />
    <div>
      <Table
        :columns="columns"
        :data="invoices"
        is-accessible
        @view="redirectToInvoiceDetailsPage"
        :selectableRowFilter="selectableRowFilter"
      >
        <template #paymentStatus="{ row }">
          <FieldSpan :style-to-add="paymentStatusFieldDisplayStyle(row.paymentStatus)" :value="$t(row.paymentStatus)" />
        </template>
        <template #amount="{ row }">
          {{ formatPrice(row.amount) }}
        </template>
      </Table>
      <Pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        :totalCount="totalCount"
        @changePage="handleChangePage"
      />
    </div>
  </div>
</template>

<script>
import InvoicesService from "@/api/services/invoices";
import MonthlyDateFilterSelector from "@/components/menu/MonthlyDateFilterSelector.vue";
import IbanMissing from "@/components/IbanMissing.vue";
import {
  ClearFiltersButton,
  Pagination,
  SecondaryButton,
  SearchBarFilter,
  PageHeader,
  Table,
  FieldSpan,
  download,
  formatPrice,
} from "@estaly/ui";
import { setQueryParams, getQueryParams, arrayFilterIfPresent } from "@estaly/ui/src/utils/query_params";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  components: {
    Pagination,
    SearchBarFilter,
    ClearFiltersButton,
    MonthlyDateFilterSelector,
    IbanMissing,
    SecondaryButton,
    PageHeader,
    Table,
    FieldSpan,
  },

  computed: {
    ...mapState("store", { stores: "index" }),
    ...mapGetters("organization", ["isStoreInvoiceLevel", "isBillingEntityInvoiceLevel"]),
    ...mapGetters("billingEntities", { billingEntities: "getIndex" }),
    ...mapGetters("invoices", ["getPaymentStatuses", "getTransactionTypes"]),

    availableFiltersWithOptions() {
      return Object.fromEntries(
        Object.entries(this.filters).filter(([filterKey, filterOptions]) => filterKey && filterOptions?.length > 1),
      );
    },

    hasActiveFilters() {
      return (
        Object.keys(this.availableFiltersWithOptions).some(
          (filterName) => this.currentFilteringData[filterName]?.length > 0,
        ) || Boolean(this.currentFilteringData.payment_start_date)
      );
    },
  },

  data() {
    return {
      invoices: [],
      filters: {
        stores: [],
        billing_entities: [],
        payment_statuses: [],
        transaction_types: [],
      },
      totalPages: null,
      totalCount: null,
      currentPage: 1,
      periodSelectorKey: 0,
      currentFilteringData: {
        stores: [],
        billing_entities: [],
        payment_statuses: [],
        transaction_types: [],
        payment_start_date: null,
        payment_end_date: null,
      },
      activeTab: 1,
      billAmount: null,
      statementAmount: null,
      columns: [
        { label: "Date de versement", field: "paymentDate" },
        { label: "Entité", field: "salesChannel" },
        { label: "Type", field: "transactionType" },
        { label: "Libellé", field: "friendlyId" },
        { label: "Montant", slot: "amount", field: "amount" },
        { label: "Statut paiement", slot: "paymentStatus", field: "status" },
      ],
    };
  },

  async mounted() {
    this.initQueryParams();
    await this.initFilters();
    this.retrieveInvoices();
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("organization", { fetchOrganization: "fetch" }),
    ...mapActions("billingEntities", { fetchBillingEntitiesIndex: "fetchIndex" }),
    formatPrice,

    async initFilters() {
      await this.fetchOrganization();

      if (this.isStoreInvoiceLevel) {
        this.filters.stores = this.stores.map((store) => store.salesChannel);
      } else if (this.isBillingEntityInvoiceLevel) {
        await this.fetchBillingEntitiesIndex({ perPage: 2000, includes: [] });
        this.filters.billing_entities = this.billingEntities.map((be) => be.name);
      }

      this.filters.payment_statuses = this.getPaymentStatuses();
      this.filters.transaction_types = this.getTransactionTypes();
    },

    paymentStatusFieldDisplayStyle(paymentStatus) {
      switch (paymentStatus) {
        case "pending_payment":
          return "bg-yellow-100 text-yellow-800";
        case "paid":
          return "bg-green-100 text-green-800";
        case "not_payable":
          return "bg-gray-100 text-gray-800";
        default:
          return "bg-blue-100 text-blue-800";
      }
    },

    handleChangePage(page) {
      this.currentPage = page;
      this.retrieveInvoices();
    },

    updateCurrentFilteringData(filterName, filterOptions) {
      this.currentFilteringData[filterName] = filterOptions;
      this.handleChangePage(1);
    },

    updateDateFilters(startDate, endDate) {
      this.currentFilteringData["payment_start_date"] = startDate;
      this.currentFilteringData["payment_end_date"] = endDate;
      this.handleChangePage(1);
    },

    clearFilters() {
      this.currentFilteringData = {
        stores: [],
        billing_entities: [],
        payment_statuses: [],
        transaction_types: [],
        payment_start_date: null,
        payment_end_date: null,
      };
      this.periodSelectorKey += 1;
      this.handleChangePage(1);
    },

    async retrieveInvoices() {
      setQueryParams({ page: this.currentPage, ...this.currentFilteringData });
      const response = await InvoicesService.fetchIndex(this.currentPage, this.currentFilteringData);
      if (!response?.success) return;

      this.invoices = response.data.invoices;
      this.billAmount = response.data.billAmount;
      this.statementAmount = response.data.statementAmount;
      this.ibanMissing = response.data.ibanMissing;
      this.totalCount = response.data.page.totalCount;
      this.totalPages = response.data.page.totalPages;
    },

    redirectToInvoiceDetailsPage(invoice) {
      this.$router.push(`/admin/billing/invoices/${invoice.friendlyId}`);
    },

    initQueryParams() {
      const queryParams = getQueryParams();
      this.currentFilteringData = {
        stores: arrayFilterIfPresent(queryParams.stores),
        billing_entities: arrayFilterIfPresent(queryParams.billing_entities),
        payment_statuses: arrayFilterIfPresent(queryParams.payment_statuses),
        transaction_types: arrayFilterIfPresent(queryParams.transaction_types),
        start_date: queryParams.start_date,
        end_date: queryParams.end_date,
      };
      this.currentPage = queryParams.page || 1;
    },

    async exportSummary() {
      const paymentStartDate = this.currentFilteringData["payment_start_date"];
      const paymentEndDate = this.currentFilteringData["payment_end_date"];

      const params = { paymentStartDate, paymentEndDate };

      const response = await InvoicesService.fetchSummary(params);
      if (!response?.success) return;

      const filename =
        paymentStartDate && paymentEndDate
          ? `invoices_summary_${this.$d(new Date(paymentStartDate), "long")}_${this.$d(new Date(paymentEndDate), "long")}.csv`
          : `invoices_summary.csv`;
      download(response.data, filename);

      this.notify({
        category: "simple",
        type: "success",
        title: "Exporting invoices summary file.",
      });
    },
  },
};
</script>
